/**
 * Card chrome around one dashboard widget: it occupies `w × h` cells of the
 * dashboard's grid. Drag the header grip to reorder (dnd-kit); drag the
 * bottom-right corner to RESIZE — width/height snap to whole grid cells, live,
 * committing once on release (monday.com-style). Click selects the widget
 * (shows the ring + resize handle); the kebab holds rename + delete.
 */
import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { MoreVertical, Pencil, Settings2, Trash2 } from 'lucide-react';
import { DragHandle } from '@/shared/ui';

import type { DashboardWidget } from '@/entities/tracker';

import {
  GRID_COLS,
  GRID_GAP,
  GRID_ROW_HEIGHT,
  WIDGET_MIN_H,
  WIDGET_MIN_W,
  widgetGeom,
} from '../../lib/dashboard';
import { useCanEditTracker } from '../../lib/tracker-access';
import { useTranslation } from 'react-i18next';

interface WidgetShellProps {
  widget: DashboardWidget;
  selected: boolean;
  onSelect: () => void;
  /** Open the settings modal to reconfigure this widget. */
  onEdit: () => void;
  onRename: (title: string) => void;
  /** Commit a new grid size (whole cells) after a corner-drag. */
  onResize: (w: number, h: number) => void;
  onDelete: () => void;
  children: React.ReactNode;
}

export function WidgetShell({
  widget,
  selected,
  onSelect,
  onEdit,
  onRename,
  onResize,
  onDelete,
  children,
}: WidgetShellProps) {
  const { t } = useTranslation('trackers');
  const canEdit = useCanEditTracker();

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: widget.id,
    disabled: !canEdit,
  });

  // Own ref (for measuring the cell) merged with dnd-kit's node ref.
  const shellRef = useRef<HTMLDivElement | null>(null);
  const mergedRef = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      shellRef.current = node;
    },
    [setNodeRef],
  );

  const geom = widgetGeom(widget);
  // Live geometry while corner-dragging; committed once on pointer-up.
  const [preview, setPreview] = useState<{ w: number; h: number } | null>(null);
  const previewRef = useRef<{ w: number; h: number } | null>(null);
  const shown = preview ?? geom;
  const resizing = preview !== null;

  const startResize = (e: ReactPointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = shellRef.current?.getBoundingClientRect();
    if (!rect) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = geom.w;
    const startH = geom.h;
    // One column's pixel width, derived from THIS widget's current size — no
    // need to know the whole grid's width.
    const cellW = (rect.width - (startW - 1) * GRID_GAP) / startW;

    const onMove = (ev: PointerEvent) => {
      const dCols = Math.round((ev.clientX - startX) / (cellW + GRID_GAP));
      const dRows = Math.round((ev.clientY - startY) / (GRID_ROW_HEIGHT + GRID_GAP));
      const next = {
        w: Math.min(GRID_COLS, Math.max(WIDGET_MIN_W, startW + dCols)),
        h: Math.max(WIDGET_MIN_H, startH + dRows),
      };
      previewRef.current = next;
      setPreview(next);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      const final = previewRef.current;
      previewRef.current = null;
      setPreview(null);
      if (final && (final.w !== geom.w || final.h !== geom.h)) onResize(final.w, final.h);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(widget.title);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Set when "Rename" is chosen from the kebab, so the menu's close-autofocus
  // doesn't yank focus back to the trigger (which would blur → commit the
  // freshly-focused input, kicking us straight out of edit mode).
  const renameFromMenuRef = useRef(false);
  useEffect(() => {
    if (!editing) setDraft(widget.title);
  }, [widget.title, editing]);
  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);
  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next && next !== widget.title) onRename(next);
    else setDraft(widget.title);
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- click selects the card; the real controls (grip, menu, resize handle) are buttons
    <div
      ref={mergedRef}
      onClick={onSelect}
      style={{
        gridColumn: `span ${shown.w}`,
        gridRow: `span ${shown.h}`,
        transform: CSS.Translate.toString(transform),
        transition: resizing ? 'none' : transition,
      }}
      className={`group/widget relative flex min-h-0 flex-col gap-2 rounded-xl border bg-card p-4 ${
        selected || resizing ? 'border-primary ring-1 ring-primary' : 'border-border'
      } ${isDragging ? 'z-10 opacity-70 shadow-lg' : ''}`}
    >
      <header className="flex items-center gap-1.5">
        {canEdit ? (
          <DragHandle
            ariaLabel={t('trackersBoard.widgetShell.dragWidget', { title: widget.title })}
            attributes={attributes}
            listeners={listeners}
            onClick={(e) => e.stopPropagation()}
            className="shrink-0 opacity-0 transition-opacity duration-fast ease-out group-hover/widget:opacity-100 focus-visible:opacity-100"
          />
        ) : null}
        {!canEdit ? (
          <h3 className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
            {widget.title}
          </h3>
        ) : editing ? (
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              else if (e.key === 'Escape') {
                setDraft(widget.title);
                setEditing(false);
              }
            }}
            className="h-6 min-w-0 flex-1 rounded-md border border-border bg-background px-1.5 text-sm font-medium text-foreground outline-none focus-visible:border-primary"
          />
        ) : (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <h3
                  className="min-w-0 flex-1 cursor-text truncate text-sm font-medium text-foreground"
                  onDoubleClick={() => setEditing(true)}
                >
                  {widget.title}
                </h3>
              </TooltipTrigger>
              <TooltipContent>{t('trackersBoard.widgetShell.doubleClickToRename')}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {canEdit ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={t('trackersBoard.widgetShell.widgetMenu', { title: widget.title })}
                onClick={(e) => e.stopPropagation()}
                className="shrink-0 rounded p-0.5 text-muted-foreground opacity-0 transition-opacity hover:bg-accent hover:text-foreground group-hover/widget:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:opacity-100"
              >
                <MoreVertical className="size-4" aria-hidden />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              onClick={(e) => e.stopPropagation()}
              onCloseAutoFocus={(e) => {
                // When renaming: stop Radix returning focus to the trigger, then
                // focus the title input ourselves. Deferred a frame so the input
                // (mounted by the `setEditing(true)` above) exists — focusing in
                // a separate effect races the menu's focus teardown and loses.
                if (renameFromMenuRef.current) {
                  e.preventDefault();
                  renameFromMenuRef.current = false;
                  requestAnimationFrame(() => {
                    inputRef.current?.focus();
                    inputRef.current?.select();
                  });
                }
              }}
            >
              <DropdownMenuItem onSelect={onEdit}>
                <Settings2 className="size-4" aria-hidden />
                {t('trackersBoard.widgetShell.editWidget')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  renameFromMenuRef.current = true;
                  setEditing(true);
                }}
              >
                <Pencil className="size-4" aria-hidden />
                {t('trackersBoard.widgetShell.rename')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={onDelete}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="size-4" aria-hidden />
                {t('trackersBoard.widgetShell.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </header>

      <div className="min-h-0 flex-1 overflow-hidden">{children}</div>

      {/* Bottom-right resize handle — snaps to grid cells. */}
      {canEdit ? (
        <button
          type="button"
          aria-label={t('trackersBoard.widgetShell.resizeWidget', { title: widget.title })}
          onPointerDown={startResize}
          onClick={(e) => e.stopPropagation()}
          className={`absolute bottom-1 right-1 flex size-4 cursor-se-resize items-end justify-end rounded-sm transition-opacity focus-visible:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring ${
            selected || resizing ? 'opacity-100' : 'opacity-0 group-hover/widget:opacity-100'
          }`}
        >
          <span className="size-2.5 rounded-br-[3px] border-b-2 border-r-2 border-muted-foreground/60" />
        </button>
      ) : null}
    </div>
  );
}
